import type {
  EnergyStatVM,
  EnergyViewVM,
  FlowGroupVM,
  HomeySnapshot,
  SecurityCameraVM,
  SecurityItemVM,
  SecurityViewVM,
  TopConsumerVM,
} from "@/types/homey";
import type { InsightPoint } from "@/components/system/InsightChart";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function numericCap(
  device: { capabilitiesObj: Record<string, { id: string; value: unknown }> },
  capId: string,
): number | null {
  const cap = device.capabilitiesObj[capId];
  if (cap === undefined) return null;
  return typeof cap.value === "number" ? cap.value : null;
}

function boolCap(
  device: { capabilitiesObj: Record<string, { id: string; value: unknown }> },
  capId: string,
): boolean | null {
  const cap = device.capabilitiesObj[capId];
  if (cap === undefined) return null;
  return typeof cap.value === "boolean" ? cap.value : null;
}

// ---------------------------------------------------------------------------
// buildEnergyView
// ---------------------------------------------------------------------------

/**
 * Builds the Energy sub-view VM from a HomeySnapshot.
 * Pure — no IO.
 */
export function buildEnergyView(snapshot: HomeySnapshot): EnergyViewVM {
  const { devices } = snapshot;

  // Find the grid meter: the device that has meter_power.daily
  const gridMeter = devices.find((d) => d.capabilities.includes("meter_power.daily")) ?? null;
  const gridMeterDeviceId = gridMeter?.id ?? null;

  // powerNowW: the measure_power of the grid meter device
  const powerNowW = gridMeter !== null ? (numericCap(gridMeter, "measure_power") ?? null) : null;

  // Build stats in documented order — only include those present
  const stats: EnergyStatVM[] = [];

  // 1. Today — meter_power.daily (kWh, 1dp)
  if (gridMeter !== null) {
    const v = numericCap(gridMeter, "meter_power.daily");
    if (v !== null) {
      stats.push({ key: "today", label: "Today", value: v, unit: "kWh", precision: 1 });
    }
  }

  // 2. Solar today — any device's meter_power.day (kWh, 1dp)
  const solarDevice = devices.find((d) => d.capabilities.includes("meter_power.day")) ?? null;
  if (solarDevice !== null) {
    const v = numericCap(solarDevice, "meter_power.day");
    if (v !== null) {
      stats.push({ key: "solar", label: "Solar today", value: v, unit: "kWh", precision: 1 });
    }
  }

  // 3. Gas today — meter_gas.daily (m³, 2dp)
  if (gridMeter !== null) {
    const v = numericCap(gridMeter, "meter_gas.daily");
    if (v !== null) {
      stats.push({ key: "gas", label: "Gas today", value: v, unit: "m³", precision: 2 });
    }
  }

  // 4. Battery — device with battery_power reading measure_battery (%, 0dp)
  const batteryDevice = devices.find((d) => d.capabilities.includes("battery_power")) ?? null;
  if (batteryDevice !== null) {
    const v = numericCap(batteryDevice, "measure_battery");
    if (v !== null) {
      stats.push({ key: "battery", label: "Battery", value: v, unit: "%", precision: 0 });
    }
  }

  // NOTE: "Returned" is intentionally NOT emitted here. On a real P1 grid meter
  // the following capabilities are monotonically increasing LIFETIME totalizers
  // and never reset: `meter_power`, `meter_power.consumed`, `meter_power.returned`,
  // and the `meter_power.produced.t*` tariff registers. Reading them directly
  // yields a cumulative lifetime figure (e.g. ~13417 kWh), not a daily value, so
  // they must be turned into a daily delta (see dailyDeltaFromCumulative) over an
  // insight series. The ONLY capability safe to read directly for a daily value
  // is `meter_power.daily`, which the Homey device resets at midnight.
  // Today's returned energy is therefore injected by the page (EnergyView), which
  // has access to the insight series; this pure builder cannot derive it.

  // topConsumers: exclude grid meter, prefer measure_power else energy_power, sort desc, top 8
  const consumers: TopConsumerVM[] = [];
  for (const device of devices) {
    if (device.id === gridMeterDeviceId) continue;

    const measurePower = numericCap(device, "measure_power");
    const energyPower = numericCap(device, "energy_power");

    let watts: number | null = null;
    if (typeof measurePower === "number") {
      watts = measurePower;
    } else if (typeof energyPower === "number") {
      watts = energyPower;
    }

    if (watts === null) continue;

    consumers.push({ id: device.id, name: device.name, watts });
  }

  consumers.sort((a, b) => b.watts - a.watts);
  const topConsumers = consumers.slice(0, 8);

  return { powerNowW, gridMeterDeviceId, stats, topConsumers };
}

// ---------------------------------------------------------------------------
// findSolarDeviceId
// ---------------------------------------------------------------------------

/**
 * Find the solar inverter device id from a snapshot. Mirrors how buildEnergyView
 * locates the solar source: the device exposing `meter_power.day` ("Opgewekt
 * Vandaag" — kWh produced today, daily-resetting). Returns the first match, or
 * null when no solar device is present. Pure — no IO.
 *
 * Used by the Energy view to build the solar production insight logId
 * (`homey:device:${id}:meter_power.day`) for the production-over-time chart.
 */
export function findSolarDeviceId(snapshot: HomeySnapshot): string | null {
  const solarDevice =
    snapshot.devices.find((d) => d.capabilities.includes("meter_power.day")) ?? null;
  return solarDevice?.id ?? null;
}

// ---------------------------------------------------------------------------
// buildFlowGroups
// ---------------------------------------------------------------------------

/**
 * Groups snapshot flows for the Flows sub-view.
 * - Favorites group first (if any); flows appear ONLY in Favorites when favorited.
 * - Remaining flows grouped by folder; Ungrouped last; folders sorted by name.
 * - Only triggerable standard flows + all advanced flows.
 */
export function buildFlowGroups(snapshot: HomeySnapshot, favoriteIds: string[]): FlowGroupVM[] {
  const { flows } = snapshot;

  // Filter includable flows: triggerable standard OR all advanced
  const includable = flows.filter(
    (f) => f.type === "advanced" || (f.type === "standard" && f.triggerable),
  );

  // Set of favorited ids for O(1) lookup
  const favSet = new Set(favoriteIds);

  // Build Favorites group preserving favoriteIds order, skipping missing
  const flowById = new Map(includable.map((f) => [f.id, f]));
  const favFlows = favoriteIds.flatMap((id) => {
    const f = flowById.get(id);
    return f ? [f] : [];
  });

  const groups: FlowGroupVM[] = [];

  if (favFlows.length > 0) {
    groups.push({ name: "Favorites", flows: favFlows });
  }

  // Remaining flows — not in favorites
  const remaining = includable.filter((f) => !favSet.has(f.id));

  // Group by folder
  const folderMap = new Map<string, typeof remaining>();
  for (const flow of remaining) {
    const folderName = flow.folder ?? "Ungrouped";
    const list = folderMap.get(folderName) ?? [];
    list.push(flow);
    folderMap.set(folderName, list);
  }

  // Sort folder names: Ungrouped last, rest alphabetically
  const folderNames = [...folderMap.keys()].sort((a, b) => {
    if (a === "Ungrouped" && b !== "Ungrouped") return 1;
    if (b === "Ungrouped" && a !== "Ungrouped") return -1;
    return a.localeCompare(b);
  });

  for (const folderName of folderNames) {
    const folderFlows = (folderMap.get(folderName) ?? []).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    groups.push({ name: folderName, flows: folderFlows });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// buildSecurityView
// ---------------------------------------------------------------------------

type AlarmKind = "motion" | "contact" | "smoke" | "water" | "tamper" | "co" | "co2" | "heat";

/**
 * Map an alarm capability suffix to a security kind, or null if it is not a
 * security-relevant alarm. Maintenance/diagnostic alarms (battery, offline,
 * connectivity, and device-specific ones like alarm_filter_work_time on a
 * vacuum) return null and are excluded from the Security view.
 */
function alarmKind(suffix: string): AlarmKind | null {
  switch (suffix) {
    case "motion":
      return "motion";
    case "contact":
      return "contact";
    case "smoke":
      return "smoke";
    case "water":
      return "water";
    case "tamper":
      return "tamper";
    case "co":
      return "co";
    case "co2":
      return "co2";
    case "heat":
    case "fire":
      return "heat";
    default:
      return null;
  }
}

function alarmState(kind: AlarmKind, triggered: boolean): string {
  switch (kind) {
    case "motion":
      return triggered ? "Motion" : "Clear";
    case "contact":
      return triggered ? "Open" : "Closed";
    case "water":
      return triggered ? "Leak" : "Dry";
    case "smoke":
      return triggered ? "Smoke" : "Clear";
    case "co":
    case "co2":
    case "heat":
      return triggered ? "Alarm" : "Clear";
    default:
      return triggered ? "Alarm" : "OK";
  }
}

/**
 * Builds the Security sub-view VM from a HomeySnapshot.
 * Pure — no IO.
 */
export function buildSecurityView(snapshot: HomeySnapshot): SecurityViewVM {
  const items: SecurityItemVM[] = [];

  for (const device of snapshot.devices) {
    // Recognized security alarm capabilities (kind != null). Maintenance and
    // diagnostic alarms (battery, work-time, etc.) are dropped.
    const securityCaps = device.capabilities
      .filter((capId) => capId.startsWith("alarm_"))
      .map((capId) => ({ capId, kind: alarmKind(capId.slice("alarm_".length)) }))
      .filter((c): c is { capId: string; kind: AlarmKind } => c.kind !== null);

    if (securityCaps.length === 0) continue;

    const multiAlarm = securityCaps.length > 1;

    for (const { capId, kind } of securityCaps) {
      const triggered = boolCap(device, capId) === true;
      const state = alarmState(kind, triggered);

      // Disambiguate name when device has multiple alarm capabilities
      const name = multiAlarm ? `${device.name} (${kind})` : device.name;

      items.push({
        deviceId: device.id,
        capabilityId: capId,
        name,
        kind,
        triggered,
        state,
      });
    }
  }

  // Sort: triggered first, then by name
  items.sort((a, b) => {
    if (a.triggered && !b.triggered) return -1;
    if (!a.triggered && b.triggered) return 1;
    return a.name.localeCompare(b.name);
  });

  const triggeredCount = items.filter((i) => i.triggered).length;

  const cameras = buildCameras(snapshot);

  return { triggeredCount, items, cameras };
}

/**
 * Collect camera/doorbell devices that expose a snapshot image. A device
 * qualifies when its class is "camera"/"doorbell" OR it has a camera-typed
 * image entry; either way it must have at least one usable image (imageId).
 * online comes from camera_connection_status, lastMotionAt from last_motion_at.
 */
function buildCameras(snapshot: HomeySnapshot): SecurityCameraVM[] {
  const cameras: SecurityCameraVM[] = [];

  for (const device of snapshot.devices) {
    const cameraImage =
      device.images.find((img) => img.type === "camera") ?? device.images[0] ?? null;

    const isCameraClass = device.deviceClass === "camera" || device.deviceClass === "doorbell";
    const hasCameraImage = device.images.some((img) => img.type === "camera");

    if (!isCameraClass && !hasCameraImage) continue;
    if (cameraImage === null) continue;

    cameras.push({
      deviceId: device.id,
      name: device.name,
      imageId: cameraImage.imageId,
      available: device.available,
      online: boolCap(device, "camera_connection_status") === true,
      lastMotionAt: numericCap(device, "last_motion_at"),
    });
  }

  cameras.sort((a, b) => a.name.localeCompare(b.name));
  return cameras;
}

// ---------------------------------------------------------------------------
// mapInsightEntries
// ---------------------------------------------------------------------------

/**
 * Maps raw Homey Insights response to InsightPoint[].
 * Tolerates null/missing input. Drops entries with null v (gaps).
 * Keeps t as-is coerced to string — labeling is a page concern.
 */
export function mapInsightEntries(
  raw: { entries?: Array<{ t: string | number; v: number | null }> } | null,
): InsightPoint[] {
  if (raw === null || raw === undefined) return [];
  const entries = raw.entries;
  if (!Array.isArray(entries)) return [];

  const result: InsightPoint[] = [];
  for (const entry of entries) {
    if (entry.v === null || entry.v === undefined) continue;
    result.push({ t: String(entry.t), value: entry.v });
  }
  return result;
}

// ---------------------------------------------------------------------------
// dailyDeltaFromCumulative
// ---------------------------------------------------------------------------

/**
 * Derive a daily delta from a cumulative (monotonically increasing) lifetime
 * register sampled over a single day. Returns `last.value - first.value`.
 *
 * Used for capabilities like `meter_power.returned` that report a lifetime
 * totalizer rather than a daily value: the energy returned *today* is the
 * difference between the last and first sample of today's insight series.
 *
 * - empty series       -> null (nothing to derive)
 * - single point       -> 0 (no delta accrued yet)
 * - normal ascending   -> last - first
 * - decreasing / out-of-order (e.g. a midnight counter reset between samples)
 *   -> clamped to 0, never negative.
 */
export function dailyDeltaFromCumulative(points: InsightPoint[]): number | null {
  if (points.length === 0) return null;
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return null;
  const delta = last.value - first.value;
  return delta > 0 ? delta : 0;
}

// ---------------------------------------------------------------------------
// Energy-flow helpers (production vs consumption vs direct use)
// ---------------------------------------------------------------------------

/**
 * Sum several insight series into one, aligning by timestamp `t` (NOT by index).
 *
 * Used to build grid net power from the three phase logs (measure_power.l1 +
 * .l2 + .l3): the aggregate measure_power log is all-null on this P1 meter, so
 * the three phases must be summed. A series missing at a given timestamp
 * contributes 0 there. Index alignment is wrong here because mapInsightEntries
 * drops null points, so series can have holes / differing lengths.
 *
 * Returns points over the union of all timestamps, sorted ascending.
 */
export function sumInsightSeriesByTimestamp(series: InsightPoint[][]): InsightPoint[] {
  const sums = new Map<string, number>();
  for (const points of series) {
    for (const point of points) {
      sums.set(point.t, (sums.get(point.t) ?? 0) + point.value);
    }
  }
  return [...sums.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([t, value]) => ({ t, value }));
}

/** A single merged point of the energy-flow chart, in watts. */
export interface EnergyFlowPoint {
  t: string;
  production: number;
  consumption: number;
  directUse: number;
  /**
   * Power drawn from the grid (W): `max(0, consumption - directUse)`. The slice
   * of consumption NOT covered by solar — the purple "Net" band in the chart.
   */
  gridImport: number;
  /**
   * Power fed back to the grid (W): `max(0, production - directUse)`. The slice
   * of production NOT consumed at home — the green "Export" band in the chart.
   */
  gridExport: number;
}

/**
 * Merge solar production and grid net power into the energy-flow series, in
 * watts, aligned by timestamp `t` over the union of both series.
 *
 * Sign convention for gridNet: >0 = importing, <0 = exporting (confirmed
 * against the live meter — at midday the phase sum goes strongly negative).
 *
 * Per timestamp:
 *   consumption  = production + gridNet
 *   exportPower  = max(0, -gridNet)
 *   directUse    = clamp(production - exportPower, 0, production)
 *   gridImport   = max(0, consumption - directUse)   // purple "Net"
 *   gridExport   = max(0, production - directUse)     // green "Teruglevering"
 *
 * directUse = min(production, consumption), so gridImport and gridExport are
 * mutually exclusive — at any timestamp at most one is > 0. Stacking
 * directUse + gridExport + gridImport therefore makes the stack top equal
 * max(production, consumption) with directUse as the shared blue base (this is
 * what makes the HomeWizard-style layered area chart render correctly).
 *
 * A missing production or gridNet at a timestamp is treated as 0.
 *
 * NOTE: consumption = production + gridNet is accurate here because this home's
 * "battery" is an advisory device with no power log. If a metered battery is
 * ever added, its charge/discharge power would have to be folded in too.
 */
export function buildEnergyFlowSeries(
  production: InsightPoint[],
  gridNet: InsightPoint[],
): EnergyFlowPoint[] {
  const prodByT = new Map(production.map((p) => [p.t, p.value]));
  const gridByT = new Map(gridNet.map((p) => [p.t, p.value]));

  const timestamps = [...new Set([...prodByT.keys(), ...gridByT.keys()])].sort((a, b) =>
    a < b ? -1 : a > b ? 1 : 0,
  );

  return timestamps.map((t) => {
    const prod = prodByT.get(t) ?? 0;
    const grid = gridByT.get(t) ?? 0;
    const consumption = prod + grid;
    const exportPower = Math.max(0, -grid);
    const directUse = Math.min(Math.max(prod - exportPower, 0), prod);
    const gridImport = Math.max(0, consumption - directUse);
    const gridExport = Math.max(0, prod - directUse);
    return { t, production: prod, consumption, directUse, gridImport, gridExport };
  });
}

/**
 * Last value of an insight series, or null when empty. Used for meter_power.day,
 * which RESETS daily — today's produced energy is the last sample, not a delta.
 */
export function lastInsightValue(points: InsightPoint[]): number | null {
  const last = points[points.length - 1];
  return last === undefined ? null : last.value;
}

/**
 * Daily direct-use percentage: the share of produced energy consumed directly
 * (not exported). (producedToday - exportedToday) / producedToday * 100.
 *
 * Returns null unless producedToday is a positive number and exportedToday is
 * non-null (guards divide-by-zero and missing data).
 */
export function dailyDirectUsePct(
  producedToday: number | null,
  exportedToday: number | null,
): number | null {
  if (producedToday === null || exportedToday === null) return null;
  if (producedToday <= 0) return null;
  return ((producedToday - exportedToday) / producedToday) * 100;
}
